<template>
  <!-- The map hike card's "⚠ N warnings" chip, beside the rating. Only when the
       selected hike's route has warnings; a link to its overview page with the
       warnings section open (#warnings). -->
  <a
    v-if="count > 0 && href"
    :href="href"
    class="badge badge-dark d-flex align-items-center m-0 mr-1 route-alerts-chip"
    data-test="route-alerts-chip"
    :aria-label="label"
    @click="onClick"
  >
    <span class="material-icons route-alerts-chip__icon" aria-hidden="true">warning</span>
    {{ label }}
  </a>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import graph_client from '../../utils/graphql/graph_client';
import { reportHandledError } from '../../utils/bugsnag';
import { HikeRouteAlertsLoader } from '../../utils/routeAlerts/routeAlertsLoader';
import { EMPTY_ROUTE_ALERTS, warningsChipText } from '../../utils/routeAlerts/routeAlertsModel';
import type { RouteAlerts } from '../../utils/routeAlerts/routeAlertsModel';
import { routeAlertsOpenedEvent, routeAlertsShownEvent, trackRouteAlertsEvent } from '../../utils/routeAlerts/routeAlertsAnalytics';
import { warningsUrl } from '../../utils/routeAlerts/warningsLink';

const SCREEN = 'map_hike_panel';

export default defineComponent({
  name: 'RouteAlertsChip',
  props: {
    hikeId: { type: [String, Number], default: null },
    /** The hike's overview page (the card's shareableUrl). */
    hikeUrl: { type: String, default: null },
  },
  data() {
    return {
      alerts: EMPTY_ROUTE_ALERTS as RouteAlerts,
      shownFor: null as string | null,
    };
  },
  computed: {
    count(): number {
      return this.alerts.count;
    },
    href(): string | null {
      return warningsUrl(this.hikeUrl);
    },
    label(): string {
      return warningsChipText(this.count, (key, values) => this.$t(key, values ?? {}) as string);
    },
  },
  watch: {
    hikeId: { immediate: true, handler: 'load' },
  },
  created() {
    (this as any).loader = new HikeRouteAlertsLoader(graph_client as any, reportHandledError, SCREEN);
  },
  beforeUnmount() {
    (this as any).loader?.cancel();
  },
  methods: {
    async load() {
      // A different hike: never show the last one's count while this one loads.
      this.alerts = EMPTY_ROUTE_ALERTS;
      const id = this.hikeId === null || this.hikeId === undefined ? '' : String(this.hikeId);
      if (!id) {
        (this as any).loader?.cancel();
        return;
      }
      const loader = (this as any).loader ?? new HikeRouteAlertsLoader(graph_client as any, reportHandledError, SCREEN);
      (this as any).loader = loader;
      const result = await loader.load(id);
      if (result.kind !== 'loaded') return;
      this.alerts = result.alerts;
      // Once per hike shown in the card.
      if (this.alerts.count > 0 && this.shownFor !== id) {
        this.shownFor = id;
        trackRouteAlertsEvent(routeAlertsShownEvent(this.alerts, SCREEN));
      }
    },
    onClick() {
      trackRouteAlertsEvent(routeAlertsOpenedEvent(this.count, SCREEN));
    },
  },
});
</script>

<style scoped>
.route-alerts-chip {
  gap: 0.2rem;
  color: #fff;
  text-decoration: none;
}

.route-alerts-chip__icon {
  color: #e28000;
  font-size: 1.1em;
}
</style>
